import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment';
import Snackbar from '@/components/Snackbar.vue';
import { INSTRUCTOR_METHODS, IN_PERSON_METHODS, PAYMENT_METHODS } from '@/constants/PaymentMethods';
import firebase from '@/plugins/firebase';
import { collectPayment, printReceipt } from '@/utils/payments';
import { isPrepaid, paidMethodLabel } from '@/utils/kidSchool';
let KidSchoolPayments = class KidSchoolPayments extends Vue {
    loading = true;
    payingKidId = '';
    printingReceiptId = '';
    pdfReceiptId = '';
    //<editor-fold desc="Lifecycle">
    async mounted() {
        try {
            // The lesson comes from the list, because the detail is merged onto a known lesson
            // and the screen can be opened before the app has loaded the day's lessons.
            if (!this.kidSchool) {
                await this.$store.dispatch('kidSchool/getAll');
            }
            await this.$store.dispatch('kidSchool/getRoster', this.kidSchoolId);
            await this.$store.dispatch('instructorTable/loadDates', [this.date]);
            // A reprint is a convenience: without it the screen still collects money.
            await this.$store.dispatch('kidSchool/getReceipts', {
                id: this.kidSchoolId,
                date: this.date.format('YYYY-MM-DD'),
            }).catch((e) => console.error('Failed to load kid school receipts:', e));
        }
        catch (e) {
            console.error(e);
            Snackbar.error('Soupisku se nepodařilo načíst');
        }
        finally {
            this.loading = false;
        }
    }
    //</editor-fold>
    //<editor-fold desc="Methods">
    async pay(kid, method) {
        if (this.payingKidId) {
            return;
        }
        this.payingKidId = kid.kidId;
        try {
            const result = await collectPayment('payKidSchoolKid', {
                kidSchoolId: this.kidSchoolId,
                kidId: kid.kidId,
                date: this.date.format('YYYY-MM-DD'),
            }, method);
            if (result) {
                await this.$store.dispatch('kidSchool/markKidPaid', {
                    id: this.kidSchoolId,
                    kidId: kid.kidId,
                    receiptId: result.receiptId,
                });
                Snackbar.success(`Doklad ${result.number} vystaven.`);
                if (result.warning) {
                    Snackbar.info(result.warning);
                }
            }
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Platbu se nepodařilo zpracovat');
        }
        finally {
            this.payingKidId = '';
        }
    }
    async printKidReceipt(kid) {
        if (this.printingReceiptId || !kid.receiptId) {
            return;
        }
        this.printingReceiptId = kid.receiptId;
        try {
            await printReceipt(kid.receiptId);
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'Doklad se nepodařilo vytisknout');
        }
        finally {
            this.printingReceiptId = '';
        }
    }
    /** The PDF path comes from the listing, because the receipt rules hide a colleague's doklad. */
    async openKidPdf(kid) {
        if (this.pdfReceiptId || !kid.receiptPdfPath) {
            return;
        }
        this.pdfReceiptId = kid.receiptId ?? '';
        try {
            window.open(await firebase.storage().ref(kid.receiptPdfPath).getDownloadURL(), '_blank');
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e instanceof Error ? e.message : 'PDF se nepodařilo otevřít');
        }
        finally {
            this.pdfReceiptId = '';
        }
    }
    kidPrice(kid) {
        return kid.price || (this.kidSchool?.price ?? 0);
    }
    paidLabel(kid) {
        return paidMethodLabel(kid.paymentMethod);
    }
    /** Prepaid spots are settled online; nothing can be collected or stornoed here. */
    canPay(kid) {
        return !kid.isPaid && !this.concluded && this.paymentMethods.length > 0;
    }
    //</editor-fold>
    //<editor-fold desc="Getters">
    get kidSchoolId() {
        return this.$route.params.id;
    }
    get kidSchool() {
        return this.$store.getters['kidSchool/all'].find((x) => x.id === this.kidSchoolId);
    }
    get kids() {
        return this.kidSchool?.kids ?? [];
    }
    get dueAmount() {
        return this.kids.filter(kid => !kid.isPaid)
            .reduce((sum, kid) => sum + this.kidPrice(kid), 0);
    }
    get collectedAmount() {
        return this.kids.filter(kid => kid.isPaid && !isPrepaid(kid.paymentMethod))
            .reduce((sum, kid) => sum + this.kidPrice(kid), 0);
    }
    get date() {
        return moment(this.kidSchool?.dateTime);
    }
    get timeString() {
        return this.date.format('H:mm');
    }
    get table() {
        return this.$store.getters['instructorTable/get'](this.date);
    }
    get concluded() {
        return this.table?.concluded === true;
    }
    get canCollectPayments() {
        return this.$store.getters['user/isModerator']
            || this.table?.kidSchools.find(x => x.id === this.kidSchoolId)?.leaderId
                === this.$store.getters['user/userId'];
    }
    /** In-person methods for a moderator, the instructor's own two for the kid school leader. */
    get paymentMethods() {
        if (this.$store.getters['user/isModerator']) {
            return PAYMENT_METHODS.filter(method => IN_PERSON_METHODS.includes(method.id));
        }
        return PAYMENT_METHODS.filter(method => INSTRUCTOR_METHODS.includes(method.id));
    }
    get attendanceRoute() {
        return { name: 'KidSchoolAttendance', params: { id: this.kidSchoolId } };
    }
};
KidSchoolPayments = __decorate([
    Component
], KidSchoolPayments);
export default KidSchoolPayments;
